import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, forkJoin, takeUntil } from 'rxjs';

import { MarketingFormsService } from '../service/marketing-forms.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import {
  MktFormSubmission,
  MktFormSubmissionFieldValue,
  MktSubmissionStatus,
} from '../models/submission.model';
import { FormDetailModel } from '../models/form.model';
import { FormFieldModel } from '../models/form-field.model';

type StatusFilter = MktSubmissionStatus | 'all';

@Component({
  selector: 'app-submissions-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, ModalComponent],
  templateUrl: './submissions-list.component.html',
})
export class SubmissionsListComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly search$  = new Subject<string>();

  formId!: number;
  form: FormDetailModel | null = null;
  submissions: MktFormSubmission[] = [];
  fieldsById = new Map<number, FormFieldModel>();

  // Carga
  loadingMeta = false;
  loading      = false;

  // Paginación
  page         = 0;
  pageSize     = 20;
  totalPages   = 0;
  totalElements = 0;

  // Filtros
  statusFilter: StatusFilter = 'all';
  onlyPartial  = false;
  searchTerm   = '';
  dateFrom     = '';
  dateTo       = '';

  // Detalle
  selected: MktFormSubmission | null = null;
  selectedFields: MktFormSubmissionFieldValue[] = [];
  detailLoading = false;

  readonly statusChips: { value: StatusFilter; label: string }[] = [
    { value: 'all',             label: 'Todos' },
    { value: 'PROCESSED',       label: 'Procesado' },
    { value: 'PENDING',         label: 'Pendiente' },
    { value: 'PARTIAL',         label: 'Parcial' },
    { value: 'AWAITING_OPTIN',  label: 'Esperando opt-in' },
    { value: 'FAILED',          label: 'Falló' },
    { value: 'SPAM_REJECTED',   label: 'Spam' },
  ];

  constructor(
    private route: ActivatedRoute,
    private formsService: MarketingFormsService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    this.formId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadMeta();

    // Debounce del campo búsqueda para no disparar una request por cada tecla
    this.search$.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(() => this.loadSubmissions(0));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Carga ────────────────────────────────────────────────────────────────

  private loadMeta(): void {
    this.loadingMeta = true;
    forkJoin({
      form:   this.formsService.getFormById(this.formId),
      fields: this.formsService.getFields(this.formId),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ form, fields }) => {
        this.form       = form;
        this.fieldsById = new Map((fields ?? []).filter(f => f.id).map(f => [f.id!, f]));
        this.loadingMeta = false;
        this.loadSubmissions(0);
      },
      error: () => {
        this.toast.error('No se pudo cargar la información del formulario');
        this.loadingMeta = false;
      },
    });
  }

  loadSubmissions(page = this.page): void {
    this.loading = true;
    this.page    = page;

    this.formsService.getSubmissions(this.formId, {
      status:      this.statusFilter !== 'all' ? this.statusFilter : undefined,
      onlyPartial: this.onlyPartial || undefined,
      dateFrom:    this.dateFrom    || undefined,
      dateTo:      this.dateTo      || undefined,
      search:      this.searchTerm.trim() || undefined,
      page:        this.page,
      size:        this.pageSize,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: result => {
        this.submissions   = result.content       ?? [];
        this.page          = result.page          ?? page;
        this.totalPages    = result.totalPages    ?? 0;
        this.totalElements = result.totalElements ?? 0;
        this.loading = false;
      },
      error: () => {
        this.toast.error('No se pudieron cargar los envíos');
        this.loading = false;
      },
    });
  }

  // ── Filtros ──────────────────────────────────────────────────────────────

  setStatus(s: StatusFilter): void {
    this.statusFilter = s;
    this.loadSubmissions(0);
  }

  togglePartial(): void {
    this.onlyPartial = !this.onlyPartial;
    this.loadSubmissions(0);
  }

  onSearch(): void {
    this.search$.next(this.searchTerm);
  }

  onDateChange(): void {
    this.loadSubmissions(0);
  }

  resetFilters(): void {
    this.statusFilter    = 'all';
    this.onlyPartial     = false;
    this.searchTerm      = '';
    this.dateFrom        = '';
    this.dateTo          = '';
    this.loadSubmissions(0);
  }

  changePage(p: number): void {
    if (p < 0 || p >= this.totalPages) return;
    this.loadSubmissions(p);
  }

  get hasActiveFilters(): boolean {
    return this.statusFilter !== 'all'
      || this.onlyPartial
      || !!this.searchTerm
      || !!this.dateFrom
      || !!this.dateTo;
  }

  // ── Detalle ──────────────────────────────────────────────────────────────

  openDetail(s: MktFormSubmission): void {
    this.selected      = s;
    this.selectedFields = [];
    this.detailLoading  = true;
    this.formsService.getSubmissionFields(s.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: vals => {
          this.selectedFields = vals ?? [];
          this.detailLoading  = false;
        },
        error: () => {
          this.detailLoading = false;
          this.toast.error('No se pudieron cargar los campos del envío');
        },
      });
  }

  closeDetail(): void {
    this.selected       = null;
    this.selectedFields = [];
  }

  // ── UI helpers ───────────────────────────────────────────────────────────

  statusColor(s: MktSubmissionStatus): string {
    const map: Record<MktSubmissionStatus, string> = {
      PROCESSED:      'bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400',
      PENDING:        'bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400',
      PARTIAL:        'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
      AWAITING_OPTIN: 'bg-purple-50 text-purple-700 dark:bg-purple-500/15 dark:text-purple-400',
      FAILED:         'bg-error-50 text-error-700 dark:bg-error-500/15 dark:text-error-400',
      SPAM_REJECTED:  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    };
    return map[s] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  }

  statusLabel(s: MktSubmissionStatus): string {
    const map: Record<MktSubmissionStatus, string> = {
      PROCESSED:      'Procesado',
      PENDING:        'Pendiente',
      PARTIAL:        'Parcial',
      AWAITING_OPTIN: 'Esperando opt-in',
      FAILED:         'Falló',
      SPAM_REJECTED:  'Spam',
    };
    return map[s] ?? s;
  }

  labelForField(formFieldId: number, fallbackKey?: string): string {
    return this.fieldsById.get(formFieldId)?.label ?? fallbackKey ?? 'Campo sin nombre';
  }

  /** Texto de la opción elegida; nunca su id (el id queda en el tooltip). */
  optionLabelFor(fv: MktFormSubmissionFieldValue): string {
    if (!fv.selectedOptionId) return '';
    const opciones = this.fieldsById.get(fv.formFieldId)?.options ?? [];
    return opciones.find(o => o.id === fv.selectedOptionId)?.optionLabel ?? '';
  }

  parsedValuePreview(v: unknown): string {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v); } catch { return String(v); }
  }

  displayValue(fv: MktFormSubmissionFieldValue): string {
    const raw = fv.rawValue ?? this.parsedValuePreview(fv.parsedValue);
    if (!raw) return '—';
    const fieldType = this.fieldsById.get(fv.formFieldId)?.fieldType;
    if (fieldType === 'GEO_COUNTRY_REGION' && raw.includes('|')) {
      const [country, region] = raw.split('|');
      return region ? `${country} · ${region}` : country;
    }
    return raw;
  }
}
